// src/utils/csvParser.ts
import Papa from 'papaparse';
import { StaffAllocationGrid, ClassAllocation, LineDefinition, CurriculumArea, SemesterDefinition } from '../types/grid';

// Types for CSV parsing
export interface ParsedMatrixData {
  subjects: MatrixCsvRow[];
  lineIds: string[];
  totalClasses: number;
  assignedClasses: number;
}
export interface ParsedSubjectList {
  subjects: SubjectData[];
  totalSubjects: number;
}

export interface SubjectData {
  code: string;
  name: string;
  faculty: string;
  line?: string; // Optional line assignment
}
export interface MatrixCsvRow {
  subjectName: string;
  lineAssignments: Record<string, string | null>; // lineId -> teacherCode
}

/**
 * Parse CSV file - detects format and handles appropriately
 */
export const parseCsvFile = (file: File): Promise<ParsedMatrixData | ParsedSubjectList> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: results => {
        try {
          
          // Handle BOM in the data
          if (results.data && results.data.length > 0) {
            const firstRow = results.data[0] as any;
            const originalKeys = Object.keys(firstRow);
            
            // Check if first key has BOM and clean it
            if (originalKeys.length > 0 && originalKeys[0].charCodeAt(0) === 0xFEFF) {
              
              // Clean all data rows
              results.data = results.data.map((row: any) => {
                const cleanedRow: any = {};
                for (const [key, value] of Object.entries(row)) {
                  const cleanKey = key.replace(/^\uFEFF/, '').trim();
                  cleanedRow[cleanKey] = value;
                }
                return cleanedRow;
              });
              
            }
          }
          if (results.errors && results.errors.length > 0) {
            console.error('CSV Parse Errors:', results.errors);
            // Only reject if there are fatal errors, not just delimiter detection
            const fatalErrors = results.errors.filter(e => e.type === 'Quotes' || e.type === 'FieldMismatch');
            if (fatalErrors.length > 0) {
              throw new Error(`CSV parsing errors: ${fatalErrors.map(e => e.message).join(', ')}`);
            }
          }
          if (!results.data || results.data.length === 0) {
            throw new Error('No data found in CSV file');
          }

          // Check if this is a subject list or matrix format
          const firstRow = results.data[0] as any;
          const headers = Object.keys(firstRow);

          // Check for subject list formats
          // Clean headers to handle BOM and other invisible characters
          const cleanHeaders = headers.map(h => h.replace(/^\uFEFF/, '').trim().toLowerCase());
          
          const isThreeColumnSubjectFormat = headers.length === 3 && 
            (cleanHeaders.some(h => h.includes('code')) &&
             cleanHeaders.some(h => h.includes('name') || h.includes('class')) &&
             cleanHeaders.some(h => h.includes('faculty')));

          const isFourColumnSubjectFormat = headers.length === 4 &&
            (cleanHeaders.some(h => h.includes('code')) &&
             cleanHeaders.some(h => h.includes('name') || h.includes('class')) &&
             cleanHeaders.some(h => h.includes('faculty')) &&
             cleanHeaders.some(h => h.includes('line')));

          const isFiveColumnSubjectFormat = headers.length === 5 &&
            (cleanHeaders.some(h => h.includes('code')) &&
             cleanHeaders.some(h => h.includes('name') || h.includes('class')) &&
             cleanHeaders.some(h => h.includes('faculty')));

          // Debug logging for CSV format detection
          console.log('📝 CSV Parser: Format detection:', {
            headers: headers,
            cleanHeaders: cleanHeaders,
            headerCount: headers.length,
            isThreeColumn: isThreeColumnSubjectFormat,
            isFourColumn: isFourColumnSubjectFormat,
            isFiveColumn: isFiveColumnSubjectFormat,
            hasCode: cleanHeaders.some(h => h.includes('code')),
            hasName: cleanHeaders.some(h => h.includes('name') || h.includes('class')),
            hasFaculty: cleanHeaders.some(h => h.includes('faculty')),
            hasLine: cleanHeaders.some(h => h.includes('line'))
          });

          // If subject list format (3, 4, or 5 columns), treat as subject list
          if (isThreeColumnSubjectFormat || isFourColumnSubjectFormat || isFiveColumnSubjectFormat || headers.length === 1 || headers[0].toLowerCase().includes('subject')) {
            const subjectData = parseSubjectListCsv(results.data as any[]);
            resolve(subjectData);
          } else {
            // Parse as matrix data (pre-assigned grid)
            const matrixData = parseMatrixCsv(results.data as any[]);
            resolve(matrixData);
          }
        } catch (error) {
          console.error('Error processing CSV data:', error);
          reject(error);
        }
      },
      error: error => {
        console.error('Papa Parse Error:', error);
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      }
    });
  });
};

/**
 * Parse subject list CSV (supports both single-column and three-column formats)
 */
const parseSubjectListCsv = (csvData: any[]): ParsedSubjectList => {
  if (!csvData || csvData.length === 0) {
    throw new Error('No data rows found in CSV');
  }

  const firstRow = csvData[0];
  const headers = Object.keys(firstRow);
  
  // Detect format based on headers
  // Clean headers to handle BOM and other invisible characters
  const cleanHeaders = headers.map(h => h.replace(/^\uFEFF/, '').trim());

  // Make format detection case-insensitive and more flexible
  const lowerHeaders = cleanHeaders.map(h => h.toLowerCase());

  const hasCodeColumn = lowerHeaders.some(h => h.includes('code'));
  const hasNameColumn = lowerHeaders.some(h => h.includes('name') || h.includes('class'));
  const hasFacultyColumn = lowerHeaders.some(h => h.includes('faculty') || h.includes('department') || h.includes('curriculum'));
  const hasLineColumn = lowerHeaders.some(h => h.includes('line'));

  const isThreeColumnFormat = headers.length === 3 && hasCodeColumn && hasNameColumn && hasFacultyColumn;
  const isFourColumnFormat = headers.length === 4 && hasCodeColumn && hasNameColumn && hasFacultyColumn && hasLineColumn;
  const isFiveColumnFormat = headers.length === 5 && hasCodeColumn && hasNameColumn && hasFacultyColumn; // Support 5-column CSV

  console.log(`🔍 Format detection:`);
  console.log(`   hasCodeColumn: ${hasCodeColumn}`);
  console.log(`   hasNameColumn: ${hasNameColumn}`);
  console.log(`   hasFacultyColumn: ${hasFacultyColumn}`);
  console.log(`   hasLineColumn: ${hasLineColumn}`);
  console.log(`   isThreeColumnFormat: ${isThreeColumnFormat}`);
  console.log(`   isFourColumnFormat: ${isFourColumnFormat}`);
  console.log(`   isFiveColumnFormat: ${isFiveColumnFormat}`);

  const subjects: SubjectData[] = [];

  if (isThreeColumnFormat || isFourColumnFormat || isFiveColumnFormat) {

    // Find column indices
    // Clean headers to remove BOM and other invisible characters
    const cleanHeaders = headers.map(h => h.replace(/^\uFEFF/, '').trim());

    console.log(`📊 CSV Format Detection: 3/4-column mode detected`);
    console.log(`📋 Headers found:`, headers);
    console.log(`🧹 Clean headers:`, cleanHeaders);
    
    const codeHeader = headers.find(h => {
      const clean = h.replace(/^\uFEFF/, '').toLowerCase().trim();
      return (clean.includes('code') && !clean.includes('name')) ||
             clean === 'class code' ||
             clean === 'subject code' ||
             clean === 'code';
    }) || headers[0];

    const nameHeader = headers.find(h => {
      const clean = h.replace(/^\uFEFF/, '').toLowerCase().trim();
      return (clean.includes('name') && !clean.includes('code')) ||
             clean === 'class name' ||
             clean === 'subject name' ||
             clean === 'name' ||
             clean === 'subject';
    }) || headers[1];

    const facultyHeader = headers.find(h => {
      const clean = h.replace(/^\uFEFF/, '').toLowerCase().trim();
      return clean.includes('faculty') ||
             clean === 'department' ||
             clean === 'curriculum area';
    }) || headers[2];
    
    const lineHeader = headers.find(h => {
      const clean = h.replace(/^\uFEFF/, '').toLowerCase().trim();
      return clean.includes('line');
    });

    console.log(`🔍 Header mapping:`);
    console.log(`   Code header: "${codeHeader}"`);
    console.log(`   Name header: "${nameHeader}"`);
    console.log(`   Faculty header: "${facultyHeader}"`);
    console.log(`   Line header: "${lineHeader}"`);
    console.log(`📊 Will process ${csvData.length} rows`);
    
    for (const row of csvData) {
      const code = row[codeHeader]?.toString()?.trim();
      const name = row[nameHeader]?.toString()?.trim();
      const faculty = row[facultyHeader]?.toString()?.trim();
      const line = lineHeader ? row[lineHeader]?.toString()?.trim() : undefined;
      
      // Create subject with available data, with fallbacks
      if (code || name) { // At least one identifier is needed
        const subject: SubjectData = {
          code: code || (name ? name.replace(/\s+/g, '').substring(0, 10).toUpperCase() : 'UNKNOWN'),
          name: name || code || 'Unknown Subject',
          faculty: faculty || 'General'
        };
        if (line) {
          subject.line = line;
        }
        subjects.push(subject);
        console.log(`📋 Parsed subject: ${subject.code} - ${subject.name} (${subject.faculty})`);
      } else {
        console.warn(`⚠️ Skipping invalid row - no code or name found:`, { code, name, faculty });
      }
    }
  } else {
    
    // Legacy single-column format
    const subjectColumnName = headers[0];
    if (!subjectColumnName) {
      throw new Error('No subject column found in CSV');
    }

    for (const row of csvData) {
      const subjectName = row[subjectColumnName]?.toString()?.trim();
      if (subjectName && subjectName !== '') {
        // For legacy format, use subject name as both code and name, default faculty to 'Unknown'
        subjects.push({
          code: subjectName.replace(/\s+/g, '').substring(0, 10).toUpperCase(), // Generate a simple code
          name: subjectName,
          faculty: 'Unknown'
        });
      }
    }
  }

  return {
    subjects,
    totalSubjects: subjects.length
  };
};

/**
 * Parse matrix format CSV (standard timetabling export)
 * Expected format: Option,A,B,C,D,E,F,G,H,I,J,K,L
 */
const parseMatrixCsv = (csvData: any[]): ParsedMatrixData => {
  if (!csvData || csvData.length === 0) {
    throw new Error('No data rows found in CSV');
  }

  // Get headers (line IDs)
  const firstRow = csvData[0];
  const headers = Object.keys(firstRow);

  // First column should be subject/option name
  const subjectColumnName = headers[0];
  if (!subjectColumnName) {
    throw new Error('No subject column found in CSV');
  }

  // Remaining columns are line assignments
  const lineIds = headers.slice(1).filter(header => header && header.trim() !== '' && header !== subjectColumnName);
  if (lineIds.length === 0) {
    throw new Error('No line columns found in CSV. Expected columns like A, B, C, etc.');
  }

  // Parse each subject row
  const subjects: MatrixCsvRow[] = [];
  let totalClasses = 0;
  let assignedClasses = 0;
  for (const row of csvData) {
    const subjectName = row[subjectColumnName]?.toString()?.trim();
    if (!subjectName) continue; // Skip empty subject names

    const lineAssignments: Record<string, string | null> = {};

    // Check each line assignment
    for (const lineId of lineIds) {
      const teacherCode = row[lineId]?.toString()?.trim();
      lineAssignments[lineId] = teacherCode || null;
      totalClasses++;
      if (teacherCode) {
        assignedClasses++;
      }
    }
    subjects.push({
      subjectName,
      lineAssignments
    });
  }
  return {
    subjects,
    lineIds,
    totalClasses,
    assignedClasses
  };
};

/**
 * Convert parsed matrix data to StaffAllocationGrid format
 * This is for creating complete new grids from matrix data
 */
export const convertMatrixToStaffAllocationGrid = (matrixData: ParsedMatrixData, gridName: string): StaffAllocationGrid => {

  // Create grid lines
  const lines: LineDefinition[] = matrixData.lineIds.map(lineId => ({
    id: lineId,
    name: `Line ${lineId}`,
    semester: lineId.charCodeAt(0) <= 70 ? 'semester1' : 'semester2',
    // A-F = semester1, G-L = semester2
    days: getDefaultDaysForLine(lineId)
  }));

  // Add paired lines if we have the full A-L set
  if (matrixData.lineIds.includes('A') && matrixData.lineIds.includes('G')) {
    const pairings: Record<string, string> = {
      A: 'G',
      B: 'H',
      C: 'I',
      D: 'J',
      E: 'K',
      F: 'L'
    };
    lines.forEach(line => {
      if (pairings[line.id]) {
        line.pairedLineId = pairings[line.id];
      }
    });
  }

  // Create class allocations from matrix data
  const classes: ClassAllocation[] = [];
  for (const subject of matrixData.subjects) {
    const classId = `${subject.subjectName.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}`;
    classes.push({
      id: classId,
      className: subject.subjectName,
      curriculumArea: extractCurriculumArea(subject.subjectName),
      yearLevel: extractYearLevel(subject.subjectName),
      lineAssignments: subject.lineAssignments,
      maxStudents: 27,
      // Default class size
      studentCount: undefined // Will be set when student data is available
    });
  }

  // Calculate curriculum areas
  const curriculumAreas = calculateCurriculumAreas(classes);

  // Create semester definitions
  const semester1Lines = matrixData.lineIds.filter(id => ['A', 'B', 'C', 'D', 'E', 'F'].includes(id));
  const semester2Lines = matrixData.lineIds.filter(id => ['G', 'H', 'I', 'J', 'K', 'L'].includes(id));
  const semesters: SemesterDefinition[] = [];
  if (semester1Lines.length > 0) {
    semesters.push({
      id: 'semester1',
      name: 'Semester 1',
      lineIds: semester1Lines
    });
  }
  if (semester2Lines.length > 0) {
    semesters.push({
      id: 'semester2',
      name: 'Semester 2',
      lineIds: semester2Lines
    });
  }

  // Add full year if both semesters have classes
  if (semester1Lines.length > 0 && semester2Lines.length > 0) {
    semesters.unshift({
      id: 'fullyear',
      name: 'Full Year',
      lineIds: matrixData.lineIds
    });
  } else if (matrixData.lineIds.length > 0) {
    // If we don't have clear semester separation, add full year view
    semesters.unshift({
      id: 'fullyear',
      name: 'Full Year',
      lineIds: matrixData.lineIds
    });
  }
  return {
    id: `grid_${Date.now()}`,
    name: gridName,
    classes,
    lines,
    curriculumAreas,
    semesters
  };
};

/**
 * Extract year level from subject name
 */
const extractYearLevel = (subjectName: string): number | undefined => {
  const subject = subjectName.toUpperCase();

  // Look for year level patterns - more specific matching
  const yearMatches = subject.match(/\b(\d{1,2})\b/);
  if (yearMatches) {
    const year = parseInt(yearMatches[1]);
    if (year >= 7 && year <= 12) {
      return year;
    }
  }

  // Fallback patterns
  if (subject.includes('YEAR 7') || subject.match(/\b7\b/)) return 7;
  if (subject.includes('YEAR 8') || subject.match(/\b8\b/)) return 8;
  if (subject.includes('YEAR 9') || subject.match(/\b9\b/)) return 9;
  if (subject.includes('YEAR 10') || subject.match(/\b10\b/)) return 10;
  if (subject.includes('YEAR 11') || subject.match(/\b11\b/)) return 11;
  if (subject.includes('YEAR 12') || subject.match(/\b12\b/)) return 12;
  return undefined;
};

/**
 * Extract curriculum area from subject name
 */
const extractCurriculumArea = (subjectName: string): string => {
  const subject = subjectName.toUpperCase();

  // Common subject area patterns
  if (subject.includes('ENGLISH')) return 'ENGLISH';
  if (subject.includes('MATHEMATICS') || subject.includes('MATHS')) return 'MATHEMATICS';
  if (subject.includes('SCIENCE') || subject.includes('BIOLOGY') || subject.includes('CHEMISTRY') || subject.includes('PHYSICS')) return 'SCIENCE';
  if (subject.includes('HISTORY') || subject.includes('GEOGRAPHY')) return 'HUMANITIES';
  if (subject.includes('ART') || subject.includes('VISUAL')) return 'ARTS';
  if (subject.includes('MUSIC')) return 'MUSIC';
  if (subject.includes('PE') || subject.includes('PHYSICAL') || subject.includes('SPORT')) return 'PHYSICAL EDUCATION';
  if (subject.includes('TECHNOLOGY') || subject.includes('DIGITAL')) return 'TECHNOLOGY';
  if (subject.includes('LANGUAGE') || subject.includes('ITALIAN') || subject.includes('CHINESE')) return 'LANGUAGES';
  if (subject.includes('BUSINESS') || subject.includes('ACCOUNTING')) return 'BUSINESS';
  if (subject.includes('RELIGION') || subject.includes('FAITH')) return 'RELIGION';
  if (subject.includes('AGRICULTURAL') || subject.includes('HORTICULTURE')) return 'AGRICULTURE';
  if (subject.includes('VET') || subject.includes('VOCATIONAL') || subject.includes('VCE VM') || subject.includes('VM ') || subject.includes('VCAL')) return 'VOCATIONAL MAJOR';
  if (subject.includes('ENGAGE') || subject.includes('DISCOVER') || subject.includes('FOCUS')) return 'FOCUS';

  // Default to general category
  return 'OTHER';
};

/**
 * Get default days for a line (common patterns)
 */
const getDefaultDaysForLine = (lineId: string): string[] => {
  // Common patterns - these can be adjusted based on school's actual schedule
  const patterns: Record<string, string[]> = {
    A: ['Monday', 'Wednesday', 'Friday'],
    B: ['Tuesday', 'Thursday'],
    C: ['Monday', 'Wednesday', 'Friday'],
    D: ['Tuesday', 'Thursday'],
    E: ['Monday', 'Wednesday', 'Friday'],
    F: ['Tuesday', 'Thursday'],
    G: ['Monday', 'Wednesday', 'Friday'],
    // Semester 2 equivalent of A
    H: ['Tuesday', 'Thursday'],
    // Semester 2 equivalent of B
    I: ['Monday', 'Wednesday', 'Friday'],
    J: ['Tuesday', 'Thursday'],
    K: ['Monday', 'Wednesday', 'Friday'],
    L: ['Tuesday', 'Thursday']
  };
  return patterns[lineId] || [];
};

/**
 * Calculate curriculum areas from classes
 */
const calculateCurriculumAreas = (classes: ClassAllocation[]): CurriculumArea[] => {
  const areas = new Map<string, {
    id: string;
    name: string;
    classCount: number;
    color: string;
  }>();
  for (const classItem of classes) {
    const areaName = classItem.curriculumArea;
    if (!areas.has(areaName)) {
      areas.set(areaName, {
        id: areaName.toLowerCase().replace(/\s+/g, '_'),
        name: areaName,
        classCount: 0,
        color: getCurriculumAreaColor(areaName)
      });
    }
    const area = areas.get(areaName)!;
    area.classCount++;
  }
  return Array.from(areas.values());
};

/**
 * Get color for curriculum area
 */
const getCurriculumAreaColor = (areaName: string): string => {
  const colors: Record<string, string> = {
    ENGLISH: '#DC2626',
    MATHEMATICS: '#2563EB',
    SCIENCE: '#16A34A',
    HUMANITIES: '#CA8A04',
    ARTS: '#9333EA',
    MUSIC: '#C2410C',
    'PHYSICAL EDUCATION': '#EA580C',
    TECHNOLOGY: '#0891B2',
    LANGUAGES: '#BE185D',
    BUSINESS: '#059669',
    RELIGION: '#7C3AED',
    AGRICULTURE: '#65A30D',
    VET: '#059669',
    FOCUS: '#7C2D12',
    OTHER: '#6B7280'
  };
  return colors[areaName] || '#6B7280';
};

/**
 * Export grid data to CSV format for timetabling software
 */
export const exportGridToCsv = (grid: StaffAllocationGrid): string => {

  // Create header row
  const lineIds = grid.lines.map(line => line.id).sort();
  const headers = ['Option', ...lineIds];

  // Group classes by subject name
  const subjectMap = new Map<string, Record<string, string>>();
  for (const classItem of grid.classes) {
    if (!subjectMap.has(classItem.className)) {
      subjectMap.set(classItem.className, {});
    }
    const subjectAssignments = subjectMap.get(classItem.className)!;

    // Get all line assignments for this class
    for (const [lineId, teacherCode] of Object.entries(classItem.lineAssignments)) {
      subjectAssignments[lineId] = teacherCode || '';
    }
  }

  // Create CSV rows
  const rows: string[][] = [headers];
  for (const [className, assignments] of subjectMap.entries()) {
    const row = [className];

    // Add assignment for each line
    for (const lineId of lineIds) {
      row.push(assignments[lineId] || '');
    }
    rows.push(row);
  }

  // Convert to CSV string
  return Papa.unparse(rows);
};

/**
 * Parse staff load CSV format
 */
export const parseStaffLoadCsv = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: results => {
        if (results.errors && results.errors.length > 0) {
          reject(new Error(`CSV parsing errors: ${results.errors.map(e => e.message).join(', ')}`));
          return;
        }
        resolve(results.data as any[] || []);
      },
      error: error => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      }
    });
  });
};

/**
 * Validate CSV data structure
 */
export const validateCsvStructure = (data: any[]): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];
  if (!data || data.length === 0) {
    errors.push('No data found in CSV file');
    return {
      isValid: false,
      errors
    };
  }
  const firstRow = data[0];
  const headers = Object.keys(firstRow);
  if (headers.length === 0) {
    errors.push('No columns found in CSV file');
    return {
      isValid: false,
      errors
    };
  }

  // Check for common issues
  if (headers.some(h => h.includes('undefined'))) {
    errors.push('CSV contains undefined column headers - check file format');
  }
  if (data.length > 1000) {
    errors.push('CSV file is very large (>1000 rows) - this may cause performance issues');
  }
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Clean and normalize CSV data
 */
export const cleanCsvData = (data: any[]): any[] => {
  return data.map(row => {
    const cleanedRow: any = {};
    for (const [key, value] of Object.entries(row)) {
      // Clean header names
      const cleanKey = key.trim().replace(/[^\w\s]/g, '');

      // Clean values
      let cleanValue = value;
      if (typeof value === 'string') {
        cleanValue = value.trim();
        // Convert empty strings to null
        if (cleanValue === '') {
          cleanValue = null;
        }
      }
      cleanedRow[cleanKey] = cleanValue;
    }
    return cleanedRow;
  });
};

/**
 * Parses a CSV file and returns the data as an array of objects
 * @param file The CSV file to parse
 * @returns Promise resolving to an array of objects
 */
export async function parseCSV(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const csv = event.target?.result as string;
        if (!csv) {
          reject(new Error('Failed to read file'));
          return;
        }
        // Split by lines and filter out empty lines
        const lines = csv.split(/\r\n|\n/).filter(line => line.trim().length > 0);
        if (lines.length < 2) {
          reject(new Error('CSV file must contain at least a header row and one data row'));
          return;
        }
        // Parse header row
        const headers = parseCSVLine(lines[0]);
        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          // Skip if we have an empty line
          if (values.length === 0 || values.length === 1 && values[0] === '') {
            continue;
          }
          // Create object from headers and values
          const obj: Record<string, string> = {};
          for (let j = 0; j < headers.length; j++) {
            obj[headers[j]] = j < values.length ? values[j] : '';
          }
          data.push(obj);
        }
        resolve(data);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    reader.readAsText(file);
  });
}

/**
 * Parses a single CSV line, handling quoted values and commas within quotes
 * @param line The CSV line to parse
 * @returns Array of values
 */
function parseCSVLine(line: string): string[] {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      // Handle escaped quotes (two double quotes in a row)
      if (i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  // Add the last field
  result.push(current.trim());
  return result;
}